import { AgGridReact } from 'ag-grid-react';
import { Theme, themeQuartz } from "ag-grid-community";
import React, { useEffect, useRef, useState, useMemo } from 'react';
import Loader from '../../../../components/loader';
import { titleCell } from '../../../../components/elements/function';
import { useParams } from 'react-router-dom';
import { useChecklistApi } from '../../../../store/api/hooks/checklistApiHook';
import ChecklistPreviewModal from '../../../Checklist/_components/checklistPreview.modal';

const inputIDS = [
  'checkbox', 'text-field', 'yes-no-na', 'number-field', 'inspection',
  'multiple-choice', 'amount', 'checklist', 'picture-file', 'signature', 'date'
] as const;

type formComponentTypes = typeof inputIDS[number];

const getNewComponent = (formComponent: formComponentTypes) => {
  const ID = 'field_' + (new Date().getTime() + Math.random()).toString();
  let newCard: any = { id: ID, component: formComponent };
  switch (formComponent) {
    case 'checkbox':
      return { ...newCard, type: 'checkbox', label: '', options: [], customize: false, optionEditable: true };
    case 'text-field':
      return { ...newCard, type: 'text', label: '', placeholder: '' };
    case 'yes-no-na':
      return { ...newCard, type: 'radio', label: '', options: ['Yes', 'No', 'Not Applicable'], customize: false, optionEditable: false };
    case 'number-field':
      return { ...newCard, type: 'number', label: '', placeholder: '' };
    case 'inspection':
      return { ...newCard, type: 'radio', label: '', options: ['Pass', 'Fail', 'Flag'], customize: false, optionEditable: false };
    case 'multiple-choice':
      return { ...newCard, type: 'radio', label: '', options: [], customize: true, optionEditable: true };
    case 'amount':
      return { ...newCard, type: 'number', label: '', icon: 'price-change' };
    case 'checklist':
      return { ...newCard, type: 'checkbox', label: '', options: [], customize: true, optionEditable: true };
    case 'picture-file':
      return { ...newCard, type: 'file', label: '', accept: 'image/*', multiple: true };
    case 'signature':
      return { ...newCard, type: 'signature', label: '' };
    case 'date':
      return { ...newCard, type: 'date', label: '' };
    default:
      return null;
  }
};

const AssociatedChecklist: React.FC = () => {
  const myTheme = themeQuartz.withParams({
    headerBackgroundColor: `#F0F0F0`
  });
  const params: any = useParams();
  const checklistAPI: any = useChecklistApi();

  const [rowData, setRowData] = useState<any[]>([]);
  const checklistPreviewModalRef = useRef<any>(null);
  const [cards, setCards] = useState<any[]>([]);
  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const getChecklists = (assetID: any = params.id) => {
    const payload: any = {
      assetIDs: [assetID],
      includeAssetDetails: true
    };

    checklistAPI.getChecklists(
      payload,
      (resp: any) => {
        let _rowData: any = resp.checklistList || [];
        _rowData = Array.from(new Map(_rowData.map((item: any) => [item.checkListID, item])).values());
        setRowData(_rowData);
      },
      (error: any) => {
        console.error("Checklist fetch error:", error);
      }
    );
  };

  useEffect(() => {
    if (params.id) {
      getChecklists(params.id);
    }
  }, [params]);



  const columnDefs = [
    {
      headerName: 'Checklist Number',
      field: 'checkListCode',
      headerClass: 'bg-[#f0f0f0]',
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => <p className="truncate" title={params.value}>{titleCell(params)}</p>,
    },
    {
      headerName: 'Title',
      field: 'title',
      headerClass: 'bg-[#f0f0f0]',
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => (
        <p className='hover:text-primary font-medium cursor-pointer truncate' title={params.value}>
          {params.value}
        </p>
      ),
      onCellClicked: (params: any) => {
        const payload = { checkListID: params.data.checkListID };

        checklistAPI.getChecklistByID(payload, (resp: any) => {
          const _cards: any[] = [];

          resp.tasks.forEach((task: any) => {
            const formComponent = inputIDS[task.inputTypeID - 1];
            let card = getNewComponent(formComponent);
            if (!card) return;

            card.label = task.inputLabel;
            card.checkListTaskID = task.checkListTaskID;
            card.sortOrder = task.sortOrder;

            if (['checkbox', 'multiple-choice', 'checklist'].includes(formComponent)) {
              card.options = task.checkBoxOptions.map((opt: any) => opt.checkboxName);
            }

            _cards.push(card);
          });

          setCards(_cards);
          checklistPreviewModalRef.current?.showModal();
        });
      }
    },
    {
      headerName: 'Description',
      field: 'description',
      headerClass: 'bg-[#f0f0f0]',
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => <p className="truncate" title={params.value}>{titleCell(params)}</p>,
    },
  ];

  return (
    <div>
      <div className='flex items-center justify-between h-[60px]'>
        <h4 className='font-semibold text-[0.938rem]'>Associated Checklist</h4>
      </div>

      <div className={`mb-3 ag-theme-alpine overflow-auto ${!rowData || rowData.length === 0 || rowData.length <= 2 ? 'h-[150px]' : 'h-[300px]'}`}>
        <AgGridReact
          theme={theme}
          columnDefs={columnDefs}
          rowData={rowData || []}
          pagination={false}
          domLayout="normal"
          ensureDomOrder={true}
          suppressHorizontalScroll={false}
          loadingOverlayComponent={Loader}
          noRowsOverlayComponent={() => (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">No Data Found</div>
              </div>
            </div>
          )}
        />
      </div>

      <ChecklistPreviewModal _ref={checklistPreviewModalRef} cards={cards} />
    </div>
  );
};

export default AssociatedChecklist;